import React, { useEffect, useState } from 'react';
import { Plus, Edit2, Trash2, X, Save, Loader2, Calendar, Image as ImageIcon, FileText, Link as LinkIcon } from 'lucide-react';
import { getExperiences, adminCreateExperience, adminUpdateExperience, adminDeleteExperience, uploadFile, resolveImg } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';
import ImageUploader from '../../components/admin/ImageUploader';
import I18nField from '../../components/admin/I18nField';

// Helper: read the ES text from a value that may be a plain string or an { es, en, fr } dict.
const i18nEs = (v) => (typeof v === 'string' ? v : (v && v.es) || '');

const MONTHS = ['ENERO', 'FEBRERO', 'MARZO', 'ABRIL', 'MAYO', 'JUNIO', 'JULIO', 'AGOSTO', 'SEPTIEMBRE', 'OCTUBRE', 'NOVIEMBRE', 'DICIEMBRE'];
const STATUS = [{ v: 'open', l: 'Abierto' }, { v: 'completo', l: 'Completo' }, { v: 'celebrado', l: 'Celebrado' }, { v: 'draft', l: 'Borrador' }];
const CATEGORIES = [{ v: 'cartel', l: 'Cartel' }, { v: 'reglamento', l: 'Reglamento' }, { v: 'tee_times', l: 'Tee Times' }, { v: 'horarios', l: 'Horarios' }, { v: 'clasificaciones', l: 'Clasificaciones' }, { v: 'album', l: 'Álbum' }, { v: 'otros', l: 'Otros' }];

const empty = () => ({ date: '', month: 'MARZO', title: '', subtitle: '', venue: '', region: '', cover: '', description: '', schedule: [], links: [], attachments: [], show_inscription: false, inscription_text: '', inscription_url: '', status: 'open', order: 0 });

export default function AdminExperiencias() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const load = () => { setLoading(true); getExperiences().then(setItems).finally(() => setLoading(false)); };
  useEffect(load, []);

  const startNew = () => setEditing(empty());
  const startEdit = (e) => setEditing({ ...e });

  const save = async () => {
    try {
      // auto-set month from date if missing
      const data = { ...editing };
      if (data.date) {
        const d = new Date(data.date);
        data.month = MONTHS[d.getMonth()];
      }
      if (data.id) {
        await adminUpdateExperience(data.id, data);
        toast({ title: 'Experiencia actualizada' });
      } else {
        await adminCreateExperience(data);
        toast({ title: 'Experiencia creada' });
      }
      setEditing(null);
      load();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'No se pudo guardar', variant: 'destructive' });
    }
  };

  const remove = async (id) => {
    if (!confirm('¿Borrar esta experiencia?')) return;
    await adminDeleteExperience(id);
    load();
  };

  const updateField = (k, v) => setEditing((p) => ({ ...p, [k]: v }));
  const updateSchedule = (i, k, v) => setEditing((p) => ({ ...p, schedule: p.schedule.map((s, idx) => idx === i ? { ...s, [k]: v } : s) }));
  const addSchedule = () => setEditing((p) => ({ ...p, schedule: [...p.schedule, { time: '', label: '' }] }));
  const removeSchedule = (i) => setEditing((p) => ({ ...p, schedule: p.schedule.filter((_, idx) => idx !== i) }));

  const updateLink = (i, k, v) => setEditing((p) => ({ ...p, links: p.links.map((s, idx) => idx === i ? { ...s, [k]: v } : s) }));
  const addLink = () => setEditing((p) => ({ ...p, links: [...p.links, { label: '', url: '', primary: false }] }));
  const removeLink = (i) => setEditing((p) => ({ ...p, links: p.links.filter((_, idx) => idx !== i) }));

  const updateAttachment = (i, k, v) => setEditing((p) => ({ ...p, attachments: p.attachments.map((s, idx) => idx === i ? { ...s, [k]: v } : s) }));
  const addAttachment = (type = 'link') => setEditing((p) => ({ ...p, attachments: [...p.attachments, { id: crypto.randomUUID(), type, label: '', url: '', category: 'otros' }] }));
  const removeAttachment = (i) => setEditing((p) => ({ ...p, attachments: p.attachments.filter((_, idx) => idx !== i) }));

  const uploadAttachment = async (i, file) => {
    if (!file) return;
    const isPdf = file.name.toLowerCase().endsWith('.pdf');
    const res = await uploadFile(file, isPdf ? 'documentos' : 'experiencias');
    updateAttachment(i, 'url', res.url);
    updateAttachment(i, 'type', isPdf ? 'pdf' : 'image');
  };

  return (
    <div className="p-6 md:p-10">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-serif-display text-3xl text-white mb-1">Experiencias</h1>
          <p className="text-white/55">Gestiona el calendario de torneos y catas</p>
        </div>
        <button onClick={startNew} className="gd-btn-gold px-5 py-2.5 text-[11px] uppercase tracking-[0.3em] font-sans-ui font-medium inline-flex items-center gap-2"><Plus size={14} /> Nueva</button>
      </div>

      {loading ? <Loader2 className="animate-spin text-[#c9a063]" /> : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {items.map((e) => (
            <div key={e.id} className="gd-card p-5 flex gap-5">
              <div className="w-32 h-24 bg-zinc-900 flex-shrink-0 overflow-hidden">{e.cover && <img src={resolveImg(e.cover)} alt={i18nEs(e.title)} className="w-full h-full object-cover" />}</div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 text-[10px] text-[#c9a063] uppercase tracking-widest mb-1">
                  <Calendar size={11} /> {e.date} · {e.region}
                  <span className={`ml-auto px-2 py-0.5 border text-[9px] ${e.status === 'open' ? 'border-emerald-500/40 text-emerald-400' : e.status === 'completo' ? 'border-amber-500/40 text-amber-400' : e.status === 'celebrado' ? 'border-blue-500/40 text-blue-400' : 'border-white/20 text-white/50'}`}>{STATUS.find((s) => s.v === e.status)?.l || e.status}</span>
                </div>
                <h3 className="font-serif-display text-lg text-white truncate">{e.title}</h3>
                <p className="text-white/55 text-sm truncate">{e.venue}</p>
                <div className="flex gap-2 mt-3">
                  <button onClick={() => startEdit(e)} className="text-xs px-3 py-1.5 border border-white/15 text-white/70 hover:text-[#c9a063] hover:border-[#c9a063] inline-flex items-center gap-1"><Edit2 size={11} /> Editar</button>
                  <button onClick={() => remove(e.id)} className="text-xs px-3 py-1.5 border border-white/15 text-white/70 hover:text-red-400 hover:border-red-400 inline-flex items-center gap-1"><Trash2 size={11} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 bg-black/80 z-50 overflow-y-auto">
          <div className="min-h-screen flex items-start justify-center p-4">
            <div className="bg-[#0d0d0e] border border-white/10 w-full max-w-4xl my-8">
              <div className="sticky top-0 bg-[#0d0d0e] z-10 flex items-center justify-between px-6 py-4 border-b border-white/10">
                <h2 className="font-serif-display text-2xl text-white">{editing.id ? 'Editar' : 'Nueva'} experiencia</h2>
                <button onClick={() => setEditing(null)} className="text-white/60 hover:text-white"><X size={20} /></button>
              </div>
              <div className="p-6 space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label="Título *"><I18nField value={editing.title} onChange={(v) => updateField('title', v)} testId="exp-title" /></Field>
                  <Field label="Subtítulo"><I18nField value={editing.subtitle} onChange={(v) => updateField('subtitle', v)} testId="exp-subtitle" placeholder="by ..." /></Field>
                  <Field label="Fecha *"><input type="date" value={editing.date} onChange={(e) => updateField('date', e.target.value)} className={inputCls} required /></Field>
                  <Field label="Estado"><select value={editing.status} onChange={(e) => updateField('status', e.target.value)} className={inputCls}>{STATUS.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}</select></Field>
                  <Field label="Sede *"><I18nField value={editing.venue} onChange={(v) => updateField('venue', v)} testId="exp-venue" /></Field>
                  <Field label="Región *"><I18nField value={editing.region} onChange={(v) => updateField('region', v)} testId="exp-region" /></Field>
                </div>

                <Field label="Portada"><ImageUploader value={editing.cover} onChange={(v) => updateField('cover', v)} folder="experiencias" specKey="experience_cover" /></Field>

                <Field label="Descripción"><I18nField type="textarea" rows={4} value={editing.description} onChange={(v) => updateField('description', v)} testId="exp-description" /></Field>

                {/* Schedule */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className={labelCls}>Horarios</label>
                    <button type="button" onClick={addSchedule} className="text-xs text-[#c9a063] hover:underline">+ Añadir</button>
                  </div>
                  <div className="space-y-2">
                    {editing.schedule.map((s, i) => (
                      <div key={i} className="flex gap-2">
                        <input value={s.time} onChange={(e) => updateSchedule(i, 'time', e.target.value)} placeholder="08:30" className={inputCls + ' w-24'} />
                        <input value={s.label} onChange={(e) => updateSchedule(i, 'label', e.target.value)} placeholder="Descripción" className={inputCls + ' flex-1'} />
                        <button type="button" onClick={() => removeSchedule(i)} className="text-white/50 hover:text-red-400 px-2"><X size={14} /></button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Links */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className={labelCls}>Botones / enlaces principales</label>
                    <button type="button" onClick={addLink} className="text-xs text-[#c9a063] hover:underline">+ Añadir</button>
                  </div>
                  <div className="space-y-3">
                    {editing.links.map((l, i) => (
                      <div key={i} className="border border-white/10 p-3 bg-white/[0.02] space-y-2.5">
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] tracking-widest text-[#c9a063] uppercase">Botón {i + 1}</span>
                          <div className="flex items-center gap-3">
                            <label className="flex items-center gap-1.5 text-xs text-white/70 cursor-pointer">
                              <input type="checkbox" checked={l.primary} onChange={(e) => updateLink(i, 'primary', e.target.checked)} className="accent-[#c9a063]" /> Destacado
                            </label>
                            <button type="button" onClick={() => removeLink(i)} className="text-white/50 hover:text-red-400"><X size={14} /></button>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          <div>
                            <label className="text-[9px] tracking-widest text-white/40 uppercase block mb-1">Texto del botón</label>
                            <input value={l.label} onChange={(e) => updateLink(i, 'label', e.target.value)} placeholder="Ej: Más información" className={inputCls} />
                          </div>
                          <div>
                            <label className="text-[9px] tracking-widest text-white/40 uppercase block mb-1">URL del botón</label>
                            <input value={l.url} onChange={(e) => updateLink(i, 'url', e.target.value)} placeholder="https://..." className={inputCls} />
                          </div>
                        </div>
                      </div>
                    ))}
                    {editing.links.length === 0 && (
                      <p className="text-white/40 text-xs italic">Sin botones. Pulsa "+ Añadir" para crear uno (ej: "Más información", "Reglamento", etc.)</p>
                    )}
                  </div>
                </div>

                {/* Attachments */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className={labelCls}>Contenidos adjuntos (imágenes, PDFs, enlaces)</label>
                    <div className="flex gap-2">
                      <button type="button" onClick={() => addAttachment('link')} className="text-xs text-[#c9a063] hover:underline inline-flex items-center gap-1"><LinkIcon size={11} /> Enlace</button>
                      <button type="button" onClick={() => addAttachment('image')} className="text-xs text-[#c9a063] hover:underline inline-flex items-center gap-1"><ImageIcon size={11} /> Imagen</button>
                      <button type="button" onClick={() => addAttachment('pdf')} className="text-xs text-[#c9a063] hover:underline inline-flex items-center gap-1"><FileText size={11} /> PDF</button>
                    </div>
                  </div>
                  <div className="space-y-3">
                    {editing.attachments.map((a, i) => (
                      <div key={a.id} className="border border-white/10 p-3 space-y-2">
                        <div className="flex gap-2 items-center">
                          <span className="text-[10px] uppercase tracking-widest text-[#c9a063] px-2 py-0.5 border border-[#c9a063]/40">{a.type}</span>
                          <input value={a.label} onChange={(e) => updateAttachment(i, 'label', e.target.value)} placeholder="Etiqueta visible (p.ej.: Cartel, Tríptico, Reglamento…)" className={inputCls + ' flex-1'} />
                          <select value={a.category} onChange={(e) => updateAttachment(i, 'category', e.target.value)} className={inputCls + ' w-40'}>{CATEGORIES.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}</select>
                          <button type="button" onClick={() => removeAttachment(i)} className="text-white/50 hover:text-red-400 px-2"><X size={14} /></button>
                        </div>
                        {a.type === 'link' ? (
                          <input value={a.url} onChange={(e) => updateAttachment(i, 'url', e.target.value)} placeholder="https://..." className={inputCls} />
                        ) : (
                          <div className="flex gap-2 items-center">
                            <input value={a.url} onChange={(e) => updateAttachment(i, 'url', e.target.value)} placeholder="URL o subir archivo" className={inputCls + ' flex-1'} />
                            <label className="text-xs text-[#c9a063] cursor-pointer px-3 py-2 border border-[#c9a063]/40 hover:bg-[#c9a063]/10">Subir<input type="file" className="hidden" accept={a.type === 'pdf' ? '.pdf' : 'image/*'} onChange={(e) => uploadAttachment(i, e.target.files?.[0])} /></label>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <Field label="Orden de aparición (opcional, solo si hay varios eventos el mismo día)"><input type="number" value={editing.order} onChange={(e) => updateField('order', parseInt(e.target.value) || 0)} className={inputCls + ' w-24'} /></Field>

                {/* Inscripción toggle */}
                <div className="border-t border-white/10 pt-5 mt-5">
                  <label className="flex items-center gap-3 cursor-pointer mb-3">
                    <input type="checkbox" checked={editing.show_inscription !== false} onChange={(e) => updateField('show_inscription', e.target.checked)} className="accent-[#c9a063] w-4 h-4" />
                    <span className="text-white font-medium">Mostrar formulario de inscripción en la ficha</span>
                  </label>
                  <p className="text-white/50 text-xs mb-4">Si lo desmarcas, en lugar del formulario se mostrará un texto y un botón con el enlace que indiques.</p>
                  {editing.show_inscription === false && (
                    <div className="space-y-3 bg-white/[0.02] p-4 border border-white/5">
                      <Field label="Texto a mostrar"><textarea rows={3} value={editing.inscription_text || ''} onChange={(e) => updateField('inscription_text', e.target.value)} placeholder="Ej: Para inscribirte ponte en contacto con golfdivino@gmail.com" className={inputCls + ' resize-none'} /></Field>
                      <Field label="URL del botón (opcional)"><input value={editing.inscription_url || ''} onChange={(e) => updateField('inscription_url', e.target.value)} placeholder="https://... (si lo dejas vacío, el botón llevará a /contacto)" className={inputCls} /></Field>
                    </div>
                  )}
                </div>
              </div>
              <div className="sticky bottom-0 bg-[#0d0d0e] border-t border-white/10 px-6 py-4 flex items-center justify-end gap-3">
                <button onClick={() => setEditing(null)} className="px-5 py-2.5 text-xs uppercase tracking-widest text-white/70 hover:text-white">Cancelar</button>
                <button onClick={save} className="gd-btn-gold px-6 py-2.5 text-[11px] uppercase tracking-[0.3em] font-medium inline-flex items-center gap-2"><Save size={13} /> Guardar</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const inputCls = 'bg-zinc-950 border border-white/10 text-white/90 px-3 py-2 text-sm focus:border-[#c9a063] focus:outline-none w-full';
const labelCls = 'text-[10px] tracking-widest text-white/50 uppercase block mb-1.5';
const Field = ({ label, children }) => <div><label className={labelCls}>{label}</label>{children}</div>;
